import { Box, Menu, MenuItem, Typography } from "@mui/material";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useState } from "react";

const GenericNavigation = ({ liveDates = [], activeDate, setActiveDate, itemRenderer = (date) => date }) => {
    
    // États pour la gestion du menu
    const [anchorEl, setAnchorEl] = useState(null);
    const [isPreviousMenu, setIsPreviousMenu] = useState(false);

    // Vérification que liveDates est bien un tableau
    const validLiveDates = Array.isArray(liveDates) ? liveDates : [];
    
    // Vérification et fallback pour activeDate
    const activeIndex = validLiveDates.findIndex((date) => date === activeDate);
    
    // Sécurisation pour éviter des erreurs avec slice() si activeIndex est -1
    const safeActiveIndex = activeIndex >= 0 ? activeIndex : 0;

    // Liste des dates précédentes et suivantes
    const previousDates = validLiveDates.slice(0, safeActiveIndex).reverse().slice(0, 5).reverse();
    const nextDates = validLiveDates.slice(safeActiveIndex + 1, safeActiveIndex + 6);
    
    // Liste des dates visibles
    const visibleDates = validLiveDates.slice(
        Math.max(0, safeActiveIndex - 1),
        safeActiveIndex + 2
    );

    // Gestion du clic sur une date
    const handleClick = (date) => {
        if (date) setActiveDate(date);
    };

    // Gestion de l'ouverture du menu
    const handleMenuOpen = (event, isPrevious) => {
        if (!event || !event.currentTarget) return;
        setAnchorEl(event.currentTarget);
        setIsPreviousMenu(isPrevious);
    };

    // Fermeture du menu
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Sélection d'une date depuis le menu
    const handleDateSelect = (date) => {
        if (date) {
            setActiveDate(date);
            handleMenuClose();
        }
    };

    return (
        <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
            {/* Bouton de navigation vers les dates précédentes */}
            <Box
                component="div"
                onClick={(event) => safeActiveIndex !== 0 && handleMenuOpen(event, true)}
                p={1}
                width={4}
                mr={0.5}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: safeActiveIndex === 0 ? "background.default" : "background.default",
                    borderRadius: "10px 0 0 10px",
                    "&:hover": {
                        bgcolor: safeActiveIndex === 0 ? 'background.default' : 'multiplex.darkblue',
                        color: safeActiveIndex === 0 ? 'inherit' : 'white',
                        cursor: safeActiveIndex === 0 ? 'default' : 'pointer',
                    },
                }}
            >
                <Typography variant="subtitle2">
                    <FontAwesomeIcon icon={anchorEl && isPreviousMenu ? faChevronDown : faChevronLeft} />
                </Typography>
            </Box>

            {/* Affichage des dates visibles */}
            {visibleDates.map((date, index) => (
                <Box
                    key={date || `date-${index}`} // Ajout d'une clé sécurisée
                    textAlign="center"
                    cursor="pointer"
                    bgcolor={date === activeDate ? "multiplex.darkblue" : "background.default"}
                    color={date === activeDate ? "background.paper" : "text.primary"}
                    borderRadius={index === 0 ? "0 10px 10px 0" : index === 2 ? "10px 0 0 10px" : "10px"}
                    mr={0.5}
                    p={1}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        "&:hover": {
                            cursor: "pointer",
                        },
                    }}
                    onClick={() => handleClick(date)}
                >
                    <Typography variant="subtitle2">{itemRenderer(date)}</Typography>
                </Box>
            ))}

            {/* Bouton de navigation vers les dates suivantes */}
            <Box
                component="div"
                disabled={safeActiveIndex === validLiveDates.length - 1} 
                onClick={(event) => safeActiveIndex !== validLiveDates.length - 1 && handleMenuOpen(event, false)}
                width={4}
                p={1}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: safeActiveIndex === validLiveDates.length - 1 ? "background.default" : "background.default",
                    borderRadius: "0 10px 10px 0",
                    "&:hover": {
                        bgcolor: safeActiveIndex === validLiveDates.length - 1 ? 'background.default' : 'multiplex.darkblue',
                        color: safeActiveIndex === validLiveDates.length - 1 ? 'inherit' : 'white',
                        cursor: safeActiveIndex === validLiveDates.length - 1 ? 'default' : 'pointer',
                    },
                }}
            >
                <Typography variant="subtitle2">
                    <FontAwesomeIcon icon={anchorEl && !isPreviousMenu ? faChevronDown : faChevronRight} />
                </Typography>
            </Box>

            {/* Menu déroulant */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: isPreviousMenu ? "left" : "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: isPreviousMenu ? "left" : "right",
                }}
                sx={{
                    "& .MuiMenu-paper": {
                        minWidth: "unset",
                        width: "fit-content",
                    },
                    "& .MuiMenuItem-root": {
                        justifyContent: "center",
                    },
                }}
            >
                {(isPreviousMenu ? previousDates : nextDates).map(
                    (date, index) => (
                        <MenuItem 
                            key={date || `menu-item-${index}`} 
                            onClick={() => handleDateSelect(date)}
                            sx={{
                                bgcolor: "background.default",
                                "&:hover": {
                                    bgcolor: "multiplex.lightgrey",
                                    cursor: "pointer",
                                },
                            }}
                        >
                            <Typography variant="subtitle2">{itemRenderer(date)}</Typography>
                        </MenuItem>
                    )
                )}
            </Menu>
        </Box>
    );
};

export default GenericNavigation;